import styled from "styled-components";
import { colors, spacing, typography, radius, shadows } from "./design-system/tokens";
import { Layout } from "antd";
import { Resizable } from "react-resizable";

interface MainContentProps {
  $showNavbar: boolean;
  $collapsed: boolean;
  $sidebarWidth: number;
}

export const AppWrapper = styled.div`
  min-height: 100vh;
  background: white;
  position: fixed;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

export const AppLayout = styled(Layout)`
  min-height: 100%;
  height: 100%;
  background: white;
  position: relative;
  display: flex;
  flex-direction: row;
  min-width: 0;
  flex: 1;
`;

export const BetaSection = styled.div`
  width: 100%;
  height: 140px;
  background: linear-gradient(135deg, #0f766e 0%, #0d9488 30%, #6366f1 70%, #818cf8 100%);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
  font-family: ${typography.fontFamilies.sans};
`;

export const BetaBanner = styled.div`
  // width: 100%;
  height: 30px;
  // max-width: 1200px;
  padding: 0 ${spacing[1]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing[4]};
  font-family: ${typography.fontFamilies.sans};
  position: relative;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0.1),
      rgba(255, 255, 255, 0.2)
    );
    opacity: 0.3;
  }
`;

export const BetaBadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

export const BetaBadge = styled.span`
  background: rgba(0, 0, 0, 0.2);
  color: ${colors.text.inverse};
  padding: ${spacing[1]} ${spacing[2]};
  border-radius: ${radius.base};
  font-size: ${typography.fontSizes.xxs};
  font-weight: ${typography.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

export const BetaMessage = styled.span`
  color: ${colors.text.inverse};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.normal};
  text-align: center;
`;

export const SupportLink = styled.a`
  color: ${colors.text.inverse};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
  z-index: 2;
  position: relative;

  &:hover {
    color: ${colors.text.inverse};
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

export const DebugButton = styled.button`
  color: ${colors.text.inverse};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;
  z-index: 2;
  position: relative;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const FloatingAppContainer = styled.div`
  position: relative;
  z-index: 2;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  height:100vh;
  overflow: hidden;
`;

export const AppInterface = styled.div`
  width: 100%;
  height: 100%;
  background: white;
  border-radius: ${radius.lg} ${radius.lg} 0 0;
  box-shadow: ${shadows.lg};
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const ResizableAIWrapper = styled(Resizable)`
  position: relative;
  height: 100%;

  .react-resizable-handle {
    position: absolute;
    right: -5px;
    top: 0;
    bottom: 0;
    width: 10px;
    cursor: col-resize;
    z-index: 100;

    &:hover {
      background: ${colors.brand.primarySurface};
    }

    &:active {
      background: ${colors.brand.primary};
    }
  }
`;

export const MainContent = styled.div<MainContentProps>`
  flex: 1;
  height: 100%;
  overflow: hidden;
  background: white;
  margin-left: ${props => props.$showNavbar ? '60px' : '0'};
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const DirectRouteContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: auto;
  background: white;

  /* Add a container for the content with appropriate padding */
  > *:not(.no-padding) {
    padding: 32px 32px 0;
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

export const ModalContent = styled.div`
  background: white;
  padding: 32px;
  border-radius: 12px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

export const ModalTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
`;

export const ModalMessage = styled.p`
  margin: 0 0 24px 0;
  font-size: 16px;
  color: #666;
  line-height: 1.5;
`;

export const SubscribeButton = styled.button`
  background: #0969da;
  color: white;
  border: none;
  padding: 12px 32px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #0860ca;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;
